import { useTranslations } from 'next-intl';
import { Message, Validate, ValidationRule } from 'react-hook-form';

type RulesObject = {
  required?: Message | ValidationRule<boolean>;
  min?: ValidationRule<number | string>;
  max?: ValidationRule<number | string>;
  maxLength?: ValidationRule<number>;
  minLength?: ValidationRule<number>;
  pattern?: ValidationRule<RegExp>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: Record<string, Validate<any, any>>;
};

const makeValidationRules = (
  rules: string[],
  fieldName: string = 'field',
  t: ReturnType<typeof useTranslations>
) => {
  const rulesObject: RulesObject = {};
  rules.forEach(rule => {
    if (rule === 'required') {
      rulesObject['required'] = t('required');
    } else if (rule === 'integer') {
      rulesObject['pattern'] = {
        value: /^\d+$/,
        message: t('number', { fieldName }),
      };
    } else if (rule.startsWith('maxLength')) {
      const maxLength = parseInt(rule.split('|')[1]);
      rulesObject['maxLength'] = {
        value: maxLength,
        message: t('maxLength', { maxLength, fieldName }),
      };
    } else if (rule.startsWith('minLength')) {
      const minLength = parseInt(rule.split('|')[1]);
      rulesObject['minLength'] = {
        value: minLength,
        message: t('minLength', { minLength }),
      };
    } else if (rule.startsWith('min')) {
      const min = parseInt(rule.split('|')[1]);
      rulesObject['min'] = {
        value: min,
        message: t('min', { min, fieldName }),
      };
    } else if (rule.startsWith('max')) {
      const max = parseInt(rule.split('|')[1]);
      rulesObject['max'] = {
        value: max,
        message: t('max', { max, fieldName }),
      };
    } else if (rule.startsWith('pattern')) {
      const pattern = rule.split('|')[1];
      rulesObject['pattern'] = {
        value: new RegExp(pattern),
        message: t('pattern', { pattern }),
      };
    } else if (rule === 'email') {
      rulesObject['pattern'] = {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: t('invalid_email'),
      };
    } else if (rule === 'label') {
      // alphanumeric and spaces
      rulesObject['pattern'] = {
        value: /^[a-zA-Z0-9\s]+$/,
        message: t('label', { fieldName }),
      };
    } else if (rule === 'password') {
      rulesObject['pattern'] = {
        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[A-Za-z\d\W]{8,}$/,
        message: t('password'),
      };
    } else if (rule === 'amount') {
      rulesObject['pattern'] = {
        value: /^\d+(\.\d{1,2})?$/,
        message: t('amount'),
      };
    } else if (rule === 'url') {
      rulesObject['validate'] = {
        value: value => {
          const regex =
            /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z0-9-]+(?::\d{1,5})?(?:\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]*)?$/;
          return value && typeof value === 'string' && !regex.test(value) ? t('url') : undefined;
        },
      };
    } else if (rule.startsWith('sliderRange')) {
      const [min, max] = rule.split('|').map(Number);
      rulesObject['validate'] = {
        value: value => {
          const [currentMin, currentMax] = value;
          if (currentMin > currentMax) {
            return t('slider_invalid_range', { min, max });
          }
          return true;
        },
      };
    }
  });
  return rulesObject;
};

export const useValidation = () => {
  const t = useTranslations('common.validation');
  return (rules: string[], fieldName: string = 'field') => {
    return makeValidationRules(rules, fieldName, t);
  };
};
